import { UUID } from "@bitory-gmbh/nidavellir";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  EllipsisVertical,
  GripVertical,
  RefreshCw,
  Trash2,
  X,
} from "lucide-react";
import { ReactNode, useState } from "react";
import t from "@/lang/lang";
import {
  ETable,
  ETableBody,
  ETableCell,
  ETableCellInput,
  ETableCellSelect,
  ETableCellTextarea,
  ETableHead,
  ETableHeader,
  ETableRow,
} from "@/shared/components/tables/e-table";
import {
  Item,
  moveSummaryItem,
  NeedForAction,
  patchSummaryItem,
  Summary,
} from "@/services/backend/htz/summary/types/summary";
import {
  PatchItemRequest,
  updateHtzSummaryCache,
  useHtzSummaryPatchItemMutation,
  useHtzSummaryShowQuery,
} from "@/services/backend/htz/summary/api/summary-api";
import { VDI6022 } from "@/services/backend/htz/report/types/report";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import {
  joinFieldName,
  useHasFieldErrors,
} from "@/shared/lib/errors/err-validation-failed-context";
import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { TableBody } from "@/shared/components/ui/table";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { CpyButton } from "@/services/backend/cpy/components/cpy-button";
import { CpyName } from "@/services/backend/cpy/types/entity-names";
import { SummaryItemRemoveDialog } from "@/services/backend/htz/summary/components/summary-item-remove-dialog";
import { SummaryItemReportDeleteDialog } from "@/services/backend/htz/summary/components/summary-item-report-delete-dialog";

const vdi6022Items = [
  { value: VDI6022.Passed, label: t("Ja") },
  { value: VDI6022.Failed, label: t("Nein") },
];

const needForActionItems = [
  { value: NeedForAction.Immediate, label: t("Sofort") },
  { value: NeedForAction.InCycle, label: t("Im Zyklus") },
];

/**
 * SummaryItemsTable lists the items of a summary — one per report — and
 * lets the user reorder them by dragging. The order is owned by the
 * backend: dropping a row sends a single patch carrying the new order,
 * and the backend renumbers the remaining items.
 *
 * `renderReportLink` keeps the table router-agnostic; the route decides
 * how the report of an item is rendered and where it links to.
 */
export function SummaryItemsTable({
  id,
  renderReportLink,
  disabled,
}: {
  id: UUID;
  renderReportLink: (item: Item) => ReactNode;
  disabled?: boolean;
}) {
  const { data, error, isLoading } = useHtzSummaryShowQuery({ id });

  return (
    <ETable>
      <ETableHeader>
        <ETableRow>
          <ETableHead />
          <ETableHead>{t("Anlagenbericht")}</ETableHead>
          <ETableHead className="w-1/4">{t("Anlage")}</ETableHead>
          <ETableHead>{t("VDI 6022 erfüllt")}</ETableHead>
          <ETableHead>{t("Handlungsbedarf")}</ETableHead>
          <ETableHead className="w-full">{t("Empfehlung")}</ETableHead>
          <ETableHead />
        </ETableRow>
      </ETableHeader>
      {data && (
        <SummaryTableBody
          data={data}
          renderReportLink={renderReportLink}
          disabled={isImmutable(data) || disabled}
        />
      )}
      {data && data.items.length === 0 && (
        <TableBody>
          <TableEmptyRow colSpan={7} />
        </TableBody>
      )}
      {error && (
        <TableBody>
          <TableRtkErrorRow error={error} colSpan={7} />
        </TableBody>
      )}
      {isLoading && (
        <TableBody>
          <TableLoadingRow colSpan={7} />
        </TableBody>
      )}
    </ETable>
  );
}

function SummaryTableBody({
  data,
  renderReportLink,
  disabled,
}: {
  data: Summary;
  renderReportLink: (item: Item) => ReactNode;
  disabled?: boolean;
}) {
  // Moves the items. The cells of a row are saved by the row itself.
  const request: PatchItemRequest = { id: data.id, itemId: "" };
  const [set] = useOptimisticDebouncedMutation(
    request,
    updateHtzSummaryCache,
    useHtzSummaryPatchItemMutation(),
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const onDragEnd = (event: DragEndEvent) => {
    if (disabled) return;

    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = data.items.findIndex((i) => i.id === active.id);
    const newIndex = data.items.findIndex((i) => i.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    // The backend renumbers the remaining items, so one patch carrying
    // the new order is enough. The optimistic value mirrors that.
    set(
      { id: data.id, itemId: String(active.id), order: newIndex },
      moveSummaryItem(data, oldIndex, newIndex),
    );
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
    >
      <SortableContext
        items={data.items.map((i) => i.id)}
        strategy={verticalListSortingStrategy}
      >
        <ETableBody>
          {data.items.map((item) => (
            <ItemRow
              key={item.id}
              summary={data}
              item={item}
              renderReportLink={renderReportLink}
              disabled={disabled}
            />
          ))}
        </ETableBody>
      </SortableContext>
    </DndContext>
  );
}

function ItemRow({
  summary,
  item,
  renderReportLink,
  disabled,
}: {
  summary: Summary;
  item: Item;
  renderReportLink: (item: Item) => ReactNode;
  disabled?: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled });

  const fieldName = joinFieldName("items", item.id);
  // Each row saves through its own mutation, so the keys of a failed save,
  // which are relative to the item, can be prefixed with it.
  const request: PatchItemRequest = { id: summary.id, itemId: item.id };
  const [set] = useOptimisticDebouncedMutation(
    request,
    updateHtzSummaryCache,
    useHtzSummaryPatchItemMutation(),
  );
  const onItemPatch = (patch: Partial<Item>) =>
    set({ ...request, ...patch }, patchSummaryItem(summary, item.id, patch));

  // The report is read-only here. Its error is resolved on the report
  // itself (it must be immutable) or by removing the row.
  const { hasErrors: reportHasErrors } = useHasFieldErrors(
    joinFieldName(fieldName, "report"),
  );

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <ETableRow ref={setNodeRef} style={style}>
      <ETableCell className="w-10">
        <button
          {...attributes}
          {...listeners}
          type="button"
          className="hover:bg-muted cursor-grab rounded p-1 disabled:cursor-default disabled:opacity-50"
          disabled={disabled}
        >
          <GripVertical className="text-muted-foreground h-4 w-4" />
        </button>
      </ETableCell>
      <ETableCell
        className={cn(
          "whitespace-nowrap",
          reportHasErrors ? "inset-ring inset-ring-red-500" : "",
        )}
      >
        {renderReportLink(item)}
      </ETableCell>
      <ETableCellInput
        value={item.airHandlingUnitName}
        onChange={(e) => onItemPatch({ airHandlingUnitName: e.target.value })}
        placeholder={t("Anlage")}
        fieldName={joinFieldName(fieldName, "airHandlingUnitName")}
        disabled={disabled}
      />
      <ETableCellSelect
        label={t("VDI 6022 erfüllt")}
        placeholder={t("Bitte wählen")}
        items={vdi6022Items}
        value={item.vdi6022Result}
        onValueChange={(vdi6022Result: string) =>
          onItemPatch({ vdi6022Result: vdi6022Result as VDI6022 })
        }
        fieldName={joinFieldName(fieldName, "vdi6022Result")}
        disabled={disabled}
      />
      <ETableCellSelect
        label={t("Handlungsbedarf")}
        placeholder={t("Bitte wählen")}
        items={needForActionItems}
        value={item.needForAction}
        onValueChange={(needForAction: string) =>
          onItemPatch({ needForAction: needForAction as NeedForAction })
        }
        fieldName={joinFieldName(fieldName, "needForAction")}
        disabled={disabled}
      />
      <ETableCellTextarea
        value={item.recommendations}
        onChange={(e) => onItemPatch({ recommendations: e.target.value })}
        placeholder={t("Empfehlung")}
        fieldName={joinFieldName(fieldName, "recommendations")}
        disabled={disabled}
      />
      <ETableCell>
        <ItemMenu summary={summary} item={item} disabled={disabled} />
      </ETableCell>
    </ETableRow>
  );
}

/**
 * ItemMenu is the row menu of the items table. Unlike {@link ETableOptions}
 * it does not create rows: items are created by creating or picking a
 * report, and creation, removal and re-applying the report are each their
 * own backend use case.
 *
 * All entries are discrete actions, so they call their mutation directly
 * instead of going through the debounced-optimistic wrapper the editable
 * cells use.
 *
 * "Aus Anlagenbericht aktualisieren" re-runs the copy that prefilled the
 * item on creation, so the row picks up changes made to the report since.
 * It overwrites the report-derived cells, including anything the user
 * edited by hand — the label says so.
 *
 * Removing the row either keeps the report or deletes it as well; both are
 * confirmed in a dialog. The dialogs are rendered outside the menu and
 * controlled from here, since the menu content unmounts when it closes.
 */
function ItemMenu({
  summary,
  item,
  disabled,
}: {
  summary: Summary;
  item: Item;
  disabled?: boolean;
}) {
  const [dialog, setDialog] = useState<"remove" | "delete" | null>(null);
  const onDialogOpenChange = (open: boolean) => {
    if (!open) {
      setDialog(null);
    }
  };

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild disabled={disabled}>
          <Button variant="ghost" size="sm" className="rounded-none">
            <EllipsisVertical />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>{t("Aktionen")}</DropdownMenuLabel>
          <DropdownMenuGroup>
            <DropdownMenuItem asChild>
              <CpyButton
                variant="ghost"
                className="h-auto w-full justify-start rounded-sm px-2 py-1.5 text-sm font-normal"
                destinationEntityName={CpyName.HTZSummaryReport}
                destinationId={summary.id}
                sourceEntityName={CpyName.HTZReport}
                sourceId={item.reportId}
                successMessage={t("Anlagenbericht übernommen.")}
              >
                {t("Aus Anlagenbericht aktualisieren")}
                <DropdownMenuShortcut>
                  <RefreshCw />
                </DropdownMenuShortcut>
              </CpyButton>
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setDialog("remove")}>
              {t("Zeile entfernen")}
              <DropdownMenuShortcut>
                <X />
              </DropdownMenuShortcut>
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setDialog("delete")}>
              {t("Zeile und Anlagenbericht löschen")}
              <DropdownMenuShortcut>
                <Trash2 />
              </DropdownMenuShortcut>
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      <SummaryItemRemoveDialog
        summaryId={summary.id}
        itemId={item.id}
        open={dialog === "remove"}
        onOpenChange={onDialogOpenChange}
      />
      <SummaryItemReportDeleteDialog
        summaryId={summary.id}
        itemId={item.id}
        open={dialog === "delete"}
        onOpenChange={onDialogOpenChange}
      />
    </>
  );
}
